import { catchupUrlFor, withinCatchupWindow } from "@hybrid-iptv/iptv-parser";
import type { Channel, Program } from "@hybrid-iptv/types";
import { usePlayerStore } from "../player/playerStore.js";
import { runsOnDevice } from "../api/client.js";
import { isResumable, useProgressStore } from "../library/progressStore.js";
import { requireFeature } from "../library/proFeatures.js";

/**
 * Section 16 catch-up, the viewer's side: replay a programme that has already
 * finished, on a channel whose provider keeps an archive.
 *
 * The URL comes from `catchupUrlFor` — the provider's declared template, or the
 * Xtream timeshift path measured against a real panel on 2026-09-17. It is played
 * like any other stream: tokenized through the proxy on the website, straight from
 * the provider in the Android app.
 */

/** The replay URL for a finished programme, or undefined when there is no replay. */
export function catchupUrlForProgram(program: Pick<Program, "start" | "end">, channel: Channel, nowMs: number): string | undefined {
  const window = { startMs: new Date(program.start).getTime(), endMs: new Date(program.end).getTime() };
  if (!Number.isFinite(window.startMs) || !Number.isFinite(window.endMs)) return undefined;
  // Only a programme that has finished: one still airing is live, and the guide's
  // own "watch from the start" for a current programme is a separate feature.
  if (window.endMs > nowMs) return undefined;
  if (!withinCatchupWindow(channel, window, nowMs)) return undefined;
  // The APK asks for the HLS form: ExoPlayer mis-measured the progressive .ts
  // (24.5 hours for a one-hour replay) and seeked by the same wrong timestamps,
  // while the HLS playlist states the length and seeks by segment — measured on
  // the phone 2026-09-18. The website keeps .ts: HLS replay through the stream
  // proxy is unverified, and an unverified format is not shipped on a guess.
  return catchupUrlFor(channel, window, runsOnDevice() ? "m3u8" : "ts");
}

/** True when this programme can be replayed here and now. */
export function canCatchup(program: Pick<Program, "start" | "end">, channel: Channel, nowMs: number): boolean {
  return catchupUrlForProgram(program, channel, nowMs) !== undefined;
}

/**
 * APK-only fallback for panels that advertise an archive but return a non-HLS
 * response from the `.m3u8` form. NativePlayer tries this only after the HLS
 * URL has failed; the website remains on its existing, unverified `.ts` path.
 */
export function catchupFallbackUrlForProgram(
  program: Pick<Program, "start" | "end">,
  channel: Channel,
  nowMs: number,
): string | undefined {
  if (!runsOnDevice()) return undefined;
  const startMs = new Date(program.start).getTime();
  const endMs = new Date(program.end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs > nowMs) return undefined;
  const window = { startMs, endMs };
  if (!withinCatchupWindow(channel, window, nowMs)) return undefined;
  const fallback = catchupUrlFor(channel, window, "ts");
  const primary = catchupUrlFor(channel, window, "m3u8");
  return fallback && fallback !== primary ? fallback : undefined;
}

/**
 * The Continue Watching key for one programme's replay.
 *
 * Channel plus START, not the programme's id: the same broadcast gets a
 * different id from the guide index (XMLTV) than from the channel's archive
 * listing (get_simple_data_table), and the Catch-up page and the guide's
 * "Watch again" must resume each other's position, not keep two.
 */
export function catchupProgressRef(channelId: string, startIso: string): string {
  return `catchup:${channelId}:${new Date(startIso).toISOString()}`;
}

/**
 * When a replay stops being available: the programme's start plus the channel's
 * archive depth. Undefined when the provider does not say how long it keeps —
 * playback is not refused on a guess there either (withinCatchupWindow).
 */
export function catchupExpiresAt(program: Pick<Program, "start">, channel: Pick<Channel, "catchupDays">): string | undefined {
  if (channel.catchupDays === undefined) return undefined;
  const startMs = new Date(program.start).getTime();
  if (!Number.isFinite(startMs)) return undefined;
  return new Date(startMs + channel.catchupDays * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * Play the replay. The archive is a finite recording, so it is played as VOD with
 * the programme's own length: the transport bar then shows the programme's duration
 * and seeking inside it works, rather than treating it as an endless live pipe.
 *
 * It is also recorded for Continue Watching (Section 16 step 3): stop partway and
 * the programme is on Home to resume, and pressing Watch on it again anywhere
 * picks up where it was left. What is remembered is the REDACTED replay URL —
 * playSource hydrates it on the device and tokenizes it on the website at play
 * time, so neither a credential nor a short-lived token is ever stored. (That is
 * also why this no longer resolves the URL itself: playSource always did, and
 * doing it here too minted two stream tokens per replay on the website.)
 */
export async function playCatchup(program: Program, channel: Channel): Promise<boolean> {
  if (!requireFeature("catchup")) return false;
  const nowMs = Date.now();
  const raw = catchupUrlForProgram(program, channel, nowMs);
  if (!raw) return false;
  const fallback = catchupFallbackUrlForProgram(program, channel, nowMs);
  const seconds = Math.max(1, Math.round((new Date(program.end).getTime() - new Date(program.start).getTime()) / 1000));
  const title = `${program.title} — ${channel.name}`;
  const ref = catchupProgressRef(channel.id, program.start);
  const saved = useProgressStore.getState().get(ref);
  const expiresAt = catchupExpiresAt(program, channel);

  const store = usePlayerStore.getState();
  store.setPlayingContent({ contentType: "catchup", contentRef: ref });
  useProgressStore.getState().remember(ref, {
    title,
    ...(channel.logoUrl ? { posterUrl: channel.logoUrl } : {}),
    url: raw,
    ...(expiresAt ? { expiresAt } : {}),
  });
  // durationIsExact: the panel serves exactly the minutes asked for, so the
  // programme's length beats any estimate the player makes of the stream.
  await store.playSource({
    url: raw,
    type: "vod",
    title,
    durationSeconds: seconds,
    durationIsExact: true,
    ...(fallback ? { altUrls: [fallback] } : {}),
  });
  if (isResumable(saved)) store.seek(saved!.positionSeconds);
  return true;
}
